import { createNoise2D, type NoiseFunction2D } from 'simplex-noise';
import { Rng, mix32 } from './rng';

const cache = new Map<number, NoiseFunction2D>();

/**
 * Seeded 2D simplex in [-1, 1]. Stands in for Rombauts' SimplexNoise::simplexnoise, which the
 * reference calls unseeded (SPEC §1). One permutation per (seed, channel).
 */
export function simplex2(seed: number, channel = 0): NoiseFunction2D {
  const key = mix32(seed ^ Math.imul(channel + 1, 0x27d4eb2d));
  let fn = cache.get(key);
  if (!fn) {
    const rng = new Rng(key);
    fn = createNoise2D(() => rng.next());
    cache.set(key, fn);
  }
  return fn;
}

/**
 * Port of NoiseSingleton::noise (P/NoiseSingleton.cpp:37-43): a [0,1] value used as a hash by
 * feeding it large multiplied inputs. The singleton's offsets were global mutable state; here each
 * consumer owns its stream, with offsets taken from its deterministic seed (SPEC ambiguity A13).
 */
export class NoiseStream {
  private readonly fn: NoiseFunction2D;

  constructor(
    seed: number,
    public xOffset: number,
    public yOffset: number,
    public scale = 0.00003,
  ) {
    this.fn = simplex2(seed, 7);
  }

  noise(x: number, y: number): number {
    return this.fn(this.scale * x + this.xOffset, this.scale * y + this.yOffset) * 0.5 + 0.5;
  }
}

/**
 * randX/randY for an object from its location. Port of SetPIDFromStartLocation
 * (P/PerpBPFunctionLibrary.cpp:69-80), which keeps the low 15 bits via C++ `%` on truncated ints.
 */
export function pidOffsets(x: number, y: number, z: number): { randX: number; randY: number } {
  const cmod = (v: number) => {
    const t = Math.trunc(v) % 32768;
    // Stored in uint16 after a signed modulo.
    return t < 0 ? t + 65536 : t;
  };
  return {
    randX: cmod(x * 87 + z * 17 + y * 13),
    randY: cmod(y * 97 + z * 17 + x * 13),
  };
}
